import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider } from './ThemeContext';
import { AuthProvider, ProtectedRoute, ProtectedAdminRoute, useAuth } from './AuthContext';
import { QuizProvider } from './contexts/QuizContext';
import { OnboardingProvider } from './contexts/OnboardingContext';
import { SpotlightTour } from './components/onboarding';
import ErrorBoundary from './components/ErrorBoundary';
import LoadingSpinner from './components/LoadingSpinner';
import NavigationModeIndicator from './NavigationModeIndicator';
import CookieConsentBanner from './components/CookieConsentBanner';
import Analytics from './components/Analytics';
import { useAnnouncement } from './hooks/useAnnouncement';
import AnnouncementModal from './components/AnnouncementModal';
// Removed SafariCacheManager and OfflineIndicator imports
import { useGlobalKeyboardShortcuts } from './useKeyboardShortcuts';
import { preloadCriticalRoutes, preloadBasedOnBehavior } from './utils/routePreloader';
import { logWebVitals, trackRouteChange } from './utils/performance';
import performanceMonitor from './services/performanceMonitor';
import { usePerformance } from './hooks/usePerformance';

// Lazy load pages for better performance
const LandingPage = lazy(() => import('./LandingPageV2'));
const HomePage = lazy(() => import('./HomePageV2'));
const QuizSetup = lazy(() => import('./QuizSetupV2'));
const QuizPage = lazy(() => import('./QuizPageEnhancedV2'));
const AnalyticsDashboard = lazy(() => import('./AnalyticsDashboardV2'));
const AchievementsPage = lazy(() => import('./AchievementsPageV2'));
const StudyModePage = lazy(() => import('./StudyModePageV2'));
const BookmarksPage = lazy(() => import('./BookmarksPageV2'));
const QuizHistoryPage = lazy(() => import('./QuizHistoryV2'));
const ReviewModePage = lazy(() => import('./ReviewModePageV2'));
const LoginPage = lazy(() => import('./LoginPageV2'));
const AuthCallback = lazy(() => import('./AuthCallback'));
const MagicLinkVerify = lazy(() => import('./MagicLinkVerify'));
const ForgotPasswordPage = lazy(() => import('./ForgotPasswordPageV2'));
const ResetPasswordPage = lazy(() => import('./ResetPasswordPageV2'));
const ProfilePage = lazy(() => import('./ProfilePageV2'));
const AdminPage = lazy(() => import('./AdminPage'));
const AdminUserHistoryPage = lazy(() => import('./pages/AdminUserHistoryPage'));
const EmailPreferences = lazy(() => import('./EmailPreferencesV2'));
const SettingsPage = lazy(() => import('./SettingsPageV2'));
const NotFoundPage = lazy(() => import('./NotFoundPage'));
const UnauthorizedPage = lazy(() => import('./UnauthorizedPage'));
const QuizTestPage = lazy(() => import('./QuizTestPage'));
const LeaderboardPage = lazy(() => import('./pages/LeaderboardPageV2'));
const QuizResultSharePage = lazy(() => import('./QuizResultSharePageV2'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'));

// Lazy load modals
const KeyboardShortcutsModal = lazy(() => import('./KeyboardShortcutsModal'));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <LoadingSpinner size="large" message="Loading..." />
  </div>
);

function AppContent() {
  // Enable global keyboard shortcuts
  const { waitingForSecondKey } = useGlobalKeyboardShortcuts();
  const location = useLocation();
  
  // Initialize performance monitoring
  const { startMeasure, endMeasure } = usePerformance({
    enabled: true,
    reportingEndpoint: '/api/analytics/performance',
    onBudgetExceeded: (metric, value, budget) => {
      console.warn(`Performance budget exceeded for ${metric}: ${value} > ${budget}`);
    }
  });
  
  // Preload critical routes on app startup and track performance
  useEffect(() => {
    preloadCriticalRoutes();
    // Track route changes for performance monitoring
    trackRouteChange(location.pathname);
    // Measure route change performance
    startMeasure(`route-${location.pathname}`);
    return () => {
      endMeasure(`route-${location.pathname}`);
    };
  }, [location.pathname, startMeasure, endMeasure]);
  
  // Log Web Vitals on initial load
  useEffect(() => {
    // Log Web Vitals after page load
    const isProduction = import.meta.env?.MODE === 'production' || (typeof process !== 'undefined' && process.env?.NODE_ENV === 'production');
    const enablePerformance = import.meta.env?.VITE_ENABLE_PERFORMANCE === 'true' || (typeof process !== 'undefined' && process.env?.REACT_APP_ENABLE_PERFORMANCE === 'true');
    if (isProduction || enablePerformance) {
      logWebVitals();
    }
  }, []);
  
  // Predictive preloading based on current route
  useEffect(() => {
    preloadBasedOnBehavior(location.pathname);
  }, [location.pathname]);
  
  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-grow">
        <Suspense fallback={<PageLoader />}>
          <Routes>
          {/* Public routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<LoginPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/auth/magic-link" element={<MagicLinkVerify />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />

          {/* Protected routes */}
          <Route path="/home" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
          <Route path="/quiz-setup/:standard" element={<ProtectedRoute><QuizSetup /></ProtectedRoute>} />
          <Route path="/quiz/:standard" element={<ProtectedRoute><QuizPage /></ProtectedRoute>} />
          <Route path="/analytics" element={<ProtectedRoute><AnalyticsDashboard /></ProtectedRoute>} />
          <Route path="/achievements" element={<ProtectedRoute><AchievementsPage /></ProtectedRoute>} />
          <Route path="/study" element={<ProtectedRoute><StudyModePage /></ProtectedRoute>} />
          <Route path="/bookmarks" element={<ProtectedRoute><BookmarksPage /></ProtectedRoute>} />
          <Route path="/quiz-history" element={<ProtectedRoute><QuizHistoryPage /></ProtectedRoute>} />
          <Route path="/review" element={<ProtectedRoute><ReviewModePage /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="/email-preferences" element={<ProtectedRoute><EmailPreferences /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
          <Route path="/leaderboard" element={<ProtectedRoute><LeaderboardPage /></ProtectedRoute>} />
          <Route path="/quiz-test" element={<ProtectedRoute><QuizTestPage /></ProtectedRoute>} />

          {/* Admin routes */}
          <Route path="/admin" element={<ProtectedAdminRoute><AdminPage /></ProtectedAdminRoute>} />
          <Route path="/admin/users/:userId/history" element={<ProtectedAdminRoute><AdminUserHistoryPage /></ProtectedAdminRoute>} />

          {/* Public shared quiz result page */}
          <Route path="/quiz-result/:shareToken" element={<QuizResultSharePage />} />

          {/* Error pages */}
          <Route path="/unauthorized" element={<UnauthorizedPage />} />
          <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </div>
      <Suspense fallback={null}>
        <KeyboardShortcutsModal />
      </Suspense>
      <NavigationModeIndicator isActive={waitingForSecondKey} />
      <CookieConsentBanner />
      <Analytics />
      <SpotlightTour />
    </div>
  );
}

// Wrapper to pass admin status to ErrorBoundary
function ErrorBoundaryWrapper({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  return (
    <ErrorBoundary level="page" isAdmin={isAdmin}>
      {children}
    </ErrorBoundary>
  );
}

// Wrapper for announcements - displays pop-up modals for active announcements
function AnnouncementWrapper({ children }: { children: React.ReactNode }) {
  const { announcement, dismissAnnouncement } = useAnnouncement();

  return (
    <>
      {children}
      {announcement && (
        <AnnouncementModal
          announcement={announcement}
          onDismiss={dismissAnnouncement}
        />
      )}
    </>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <ErrorBoundaryWrapper>
          <ThemeProvider>
            <QuizProvider>
              <OnboardingProvider>
                <AnnouncementWrapper>
                  <AppContent />
                </AnnouncementWrapper>
              </OnboardingProvider>
            </QuizProvider>
          </ThemeProvider>
        </ErrorBoundaryWrapper>
      </AuthProvider>
    </Router>
  );
}

export default App;